import ConfirmationModal from "components/common/ConfirmationMessage";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { cdn } from "constants/common";
import moment from "moment-timezone";
import React from "react";
import toast from "react-hot-toast";
import Api from "services/api";

/**
 * Admin screen for the single global appointment instructions document.
 * There is one file for the whole system - uploading replaces the previous one.
 *
 * Follows the app's standalone-form chrome (content > row_main_head >
 * container_main > link_table) and the drag-and-drop upload pattern used by the
 * translation and appointment document forms.
 */
const AppointmentInstructions = () => {
  const [document, setDocument] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [dragActive, setDragActive] = React.useState(false);
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const inputRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    const response = await Api.getAppointmentInstructions();
    setLoading(false);

    if (response?.status === 200) {
      setDocument(response?.data?.data?.document || null);
    } else {
      toast.error(response?.data?.message || "Unable to load the document", {
        className: "errorToast",
      });
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const acceptFile = (file) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Please choose a PDF file", { className: "errorToast" });
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please choose a PDF first", { className: "errorToast" });
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    setSaving(true);
    const response = await Api.uploadAppointmentInstructions(formData);
    setSaving(false);

    if (response?.status === 200) {
      toast.success(response?.data?.message || "Document uploaded", {
        className: "successToast",
      });
      setDocument(response?.data?.data?.document || null);
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } else {
      toast.error(
        response?.data?.errors?.file?.[0] ||
          response?.data?.message ||
          "Upload failed",
        { className: "errorToast" }
      );
    }
  };

  const handleRemove = async () => {
    setConfirmRemove(false);
    setSaving(true);
    const response = await Api.deleteAppointmentInstructions();
    setSaving(false);

    if (response?.status === 200) {
      toast.success(response?.data?.message || "Document removed", {
        className: "successToast",
      });
      setDocument(null);
    } else {
      toast.error(response?.data?.message || "Unable to remove the document", {
        className: "errorToast",
      });
    }
  };

  return (
    <div className="content">
      <div className="row_main_head">
        <h2 className="main_heading">Appointment Instructions</h2>
      </div>

      <div className="container_main mt-4">
        <div className="link_table">
          <p style={{ color: "#6c889c", fontSize: 15, marginBottom: 24 }}>
            One PDF is shared across every appointment. Vendors and interpreters
            see a download link on the appointment page. Uploading a new file
            replaces the current one.
          </p>

          {loading ? (
            <p style={{ color: "#6c889c" }}>Loading&hellip;</p>
          ) : (
            <>
              {/* CURRENT DOCUMENT */}
              <div className="form-group">
                <label>Current document</label>
                {document ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      flexWrap: "wrap",
                      background: "#f8fafc",
                      border: "1.5px solid #e0e7ef",
                      borderRadius: 12,
                      padding: "16px 20px",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <a
                        href={cdn + document.path}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          color: "#6F8DFF",
                          fontWeight: 600,
                          fontSize: 16,
                          wordBreak: "break-word",
                        }}
                      >
                        <i className="fa-solid fa-file-pdf" />
                        {document.file_name}
                      </a>
                      {document.uploaded_at && (
                        <div
                          style={{
                            fontSize: 13,
                            color: "#6c889c",
                            marginTop: 6,
                          }}
                        >
                          Uploaded{" "}
                          {moment
                            .utc(document.uploaded_at)
                            .tz("America/Los_Angeles")
                            .format("MMM D, YYYY hh:mm A")}
                        </div>
                      )}
                    </div>
                    <SubmitButton
                      buttonType="button"
                      className="lightBlue"
                      btnText="Remove"
                      disabled={saving}
                      handleSubmit={() => setConfirmRemove(true)}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1.5px solid #e0e7ef",
                      borderRadius: 12,
                      padding: "16px 20px",
                      color: "#6c889c",
                      fontSize: 15,
                    }}
                  >
                    No document uploaded yet
                  </div>
                )}
              </div>

              {/* UPLOAD */}
              <div className="form-group">
                <label>{document ? "Replace document" : "Upload document"}</label>

                <div
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                    acceptFile(e.dataTransfer?.files?.[0]);
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: dragActive
                      ? "2px solid #1976d2"
                      : "2px dashed #ccc",
                    borderRadius: 8,
                    padding: 32,
                    textAlign: "center",
                    background: dragActive ? "#e3f2fd" : "#fafbff",
                    transition: "all 0.2s",
                    outline: dragActive ? "2px solid #1976d2" : "none",
                  }}
                >
                  <input
                    type="file"
                    ref={inputRef}
                    accept="application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => acceptFile(e.target.files?.[0])}
                  />

                  <div
                    style={{
                      color: "#6F8DFF",
                      fontWeight: 600,
                      fontSize: 22,
                      marginBottom: 16,
                    }}
                  >
                    Browse or Drag and Drop{" "}
                    <span
                      style={{
                        color: "#6c889c",
                        fontWeight: 400,
                        fontSize: 18,
                        marginLeft: 12,
                      }}
                    >
                      (.pdf, up to 10 MB)
                    </span>
                  </div>

                  <SubmitButton
                    btnText="Browse Files"
                    buttonType="button"
                    handleSubmit={() => inputRef.current?.click()}
                  />

                  {selectedFile && (
                    <div
                      style={{
                        marginTop: 18,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        color: "#6c889c",
                        fontSize: 15,
                      }}
                    >
                      <i
                        className="fa-solid fa-file-pdf"
                        style={{ color: "#6F8DFF" }}
                      />
                      <span style={{ wordBreak: "break-word" }}>
                        {selectedFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          if (inputRef.current) inputRef.current.value = "";
                        }}
                        title="Remove this selection"
                        style={{
                          background: "none",
                          border: "none",
                          color: "#c0392b",
                          cursor: "pointer",
                          fontSize: 16,
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <SubmitButton
                buttonType="button"
                className="green mt-4"
                btnText={document ? "Replace Document" : "Upload Document"}
                loader={saving}
                disabled={!selectedFile}
                handleSubmit={handleUpload}
              />
            </>
          )}
        </div>
      </div>

      {confirmRemove && (
        <ConfirmationModal
          isOpen={confirmRemove}
          onClose={() => setConfirmRemove(false)}
          onConfirm={handleRemove}
        />
      )}
    </div>
  );
};

export default AppointmentInstructions;
